/**
 * User-facing consent copy (SC-360).
 *
 * The wording versions are stored with every marketing choice, so a record can be traced back
 * to the exact sentence the user saw. If the checkbox or switch wording changes, bump its
 * version in the same change.
 */

export const MARKETING_EMAIL_WORDING_VERSION = 'v1'
export const PROFILE_MARKETING_WORDING_VERSION = 'profile-v1'

/** Wording version `v1`. */
export const MARKETING_EMAIL_CHECKBOX_LABEL =
  'Email me about Sevv product updates, news and offers. You can unsubscribe at any time.'

export const TERMS_OF_SERVICE_LABEL = 'Terms of Service'
export const PRIVACY_POLICY_LABEL = 'Privacy Policy'

/** The opening of each Terms notice line: "<action>, you agree to the Terms of Service …". */
export const TERMS_NOTICE_ACTION = {
  signIn: 'By signing in',
  createAccount: 'By creating an account',
  setPassword: 'By setting your password',
} as const

const PRIVACY_EXPLAINER =
  'The Privacy Policy explains what data we collect and how we use it, including the usage data that helps us improve Sevv.'

export const TERMS_SCREEN_COPY = {
  updated: {
    title: "We've updated our Terms of Service and Privacy Policy",
    notice:
      "By continuing, you agree to the updated Terms of Service and confirm you've read the Privacy Policy.",
  },
  welcome: {
    title: 'Welcome to Sevv',
    notice:
      "By continuing, you agree to the Terms of Service and confirm you've read the Privacy Policy.",
  },
  readTerms: 'Read the Terms of Service',
  readPrivacy: 'Read the Privacy Policy',
  signOut: 'Sign out',
  continue: 'Continue',
  acceptFailed: "We couldn't record your answer. Please try again.",
  checking: 'Checking your account…',
} as const

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/**
 * '2026-11-01' → '1 November 2026'. The text is built from the parsed parts rather than from a
 * Date so the shown day cannot shift with the browser's time zone. Returns null for anything that
 * is not a real date: a UTC round trip rejects days the month does not have (2026-02-31 would
 * otherwise read "31 February 2026").
 */
export function formatEffectiveDate(value: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '')
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null
  }
  return `${day} ${MONTHS[month - 1]} ${year}`
}

export function termsScreenBody(
  variant: 'updated' | 'welcome',
  effectiveDate: string | null | undefined
): string {
  if (variant === 'welcome') {
    return `Before you start, please take a moment to read our Terms of Service and Privacy Policy. ${PRIVACY_EXPLAINER}`
  }
  const formatted = formatEffectiveDate(effectiveDate)
  return formatted
    ? `The changes take effect on ${formatted}. ${PRIVACY_EXPLAINER}`
    : PRIVACY_EXPLAINER
}

export const EMAIL_PREFERENCES_COPY = {
  heading: 'Email preferences',
  rowLabel: 'Product updates, news and offers',
  rowDescription: 'Occasional emails about new Sevv features, news and offers.',
  on: 'On',
  off: 'Off',
  switchAriaLabel: 'Product updates, news and offers emails',
  serviceNote:
    'Service emails, such as security alerts, invitations and billing, are always sent because your account needs them.',
  saved: 'Saved. Your choice is recorded.',
  saveFailed: "Couldn't save your choice. Please try again.",
  unavailable: 'Email preferences are not available right now. Please try again later.',
} as const
